/**
 * «Реєстрація»: name, phone, email, password and two consents — data
 * processing, and control of the battery by a schedule the client sees and
 * changes. Then straight to «Додати об'єкт».
 */
import { useMutation } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { type SignupBody, type SignupResponse, emailSchema, personNameSchema, phoneSchema } from 'shared';

import { AuthLayout } from '../../components/AuthLayout.js';
import { errorText } from '../../components/errors.js';
import { useLocale } from '../../i18n/locale-context.js';
import { ApiError, api } from '../../lib/api-client.js';
import { queryClient } from '../../lib/query-client.js';

/** Version of the consent texts above (`signupBodySchema.terms_version`). */
const TERMS_VERSION = '2026-10-20';

export function SignupPage() {
  const { t, tf } = useLocale();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [terms, setTerms] = useState(false);
  const [control, setControl] = useState(false);
  const [checked, setChecked] = useState(false);

  const ok = {
    name: personNameSchema.safeParse(name).success,
    phone: phoneSchema.safeParse(phone).success,
    email: emailSchema.safeParse(email).success,
    password: password.length >= 8,
    consents: terms && control,
  };

  const signup = useMutation({
    mutationFn: () => {
      const body: SignupBody = {
        email: email.trim().toLowerCase(),
        password,
        country_code: 'UA',
        terms_accepted: true,
        terms_version: TERMS_VERSION,
        name: name.trim(),
        phone: phone.trim(),
      };
      return api.post<SignupResponse>('/auth/signup', body);
    },
    onSuccess: (data) => {
      queryClient.clear();
      queryClient.setQueryData(['profile'], data.user);
      navigate('/app/sites/new', { replace: true });
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setChecked(true);
    if (Object.values(ok).every(Boolean)) signup.mutate();
  };
  const formError =
    signup.error instanceof ApiError && signup.error.status === 409
      ? t('auth.signup.email_taken')
      : signup.error
        ? errorText(signup.error, tf)
        : null;
  const bad = (k: keyof typeof ok) => checked && !ok[k];

  return (
    <AuthLayout>
      <form className="auth-form" noValidate onSubmit={submit}>
        <h1>{t('auth.signup.title')}</h1>
        <p className="sub">{t('auth.signup.sub')}</p>
        <label className="field">
          <span>{t('auth.signup.name')}</span>
          <input
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-invalid={bad('name')}
          />
          {bad('name') ? <small className="err">{t('auth.err.name')}</small> : null}
        </label>
        <label className="field">
          <span>{t('auth.signup.phone')}</span>
          <input
            type="tel"
            autoComplete="tel"
            placeholder="+380"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            aria-invalid={bad('phone')}
          />
          {bad('phone') ? <small className="err">{t('auth.err.phone')}</small> : null}
        </label>
        <label className="field">
          <span>{t('auth.email')}</span>
          <input
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-invalid={bad('email')}
          />
          {bad('email') ? <small className="err">{t('auth.err.email')}</small> : null}
        </label>
        <label className="field">
          <span>{t('auth.password')}</span>
          <input
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            aria-invalid={bad('password')}
          />
          <small className={bad('password') ? 'err' : undefined}>
            {bad('password') ? t('auth.err.password') : t('auth.password.hint')}
          </small>
        </label>
        <label className="check">
          <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} />
          <span>{t('auth.signup.terms')}</span>
        </label>
        <label className="check">
          <input type="checkbox" checked={control} onChange={(e) => setControl(e.target.checked)} />
          <span>{t('auth.signup.control')}</span>
        </label>
        {bad('consents') ? <p className="err">{t('auth.signup.consent_err')}</p> : null}
        {formError ? (
          <p className="err" role="alert">
            {formError}
          </p>
        ) : null}
        <button className="btn primary wide" type="submit" disabled={signup.isPending}>
          {t('auth.signup.submit')}
        </button>
        <div className="auth-links">
          <span>
            {t('auth.signup.have')} <Link to="/login">{t('auth.signup.go_login')}</Link>
          </span>
        </div>
      </form>
    </AuthLayout>
  );
}
